import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { X, AlertTriangle, Loader2, Info } from 'lucide-react';
import { penaltyService } from '@/services/penalty.service';
import { usersService } from '@/services/users.service';
import { Button } from '@/components/ui/button';
import { DatePicker } from '@/components/ui/date-picker';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

function getTodayISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// Shared input class — works in light + dark via CSS vars
const inputCls =
  'w-full rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring transition-colors';

interface Props {
  onClose: () => void;
}

export function ApplyPenaltyModal({ onClose }: Props) {
  const qc = useQueryClient();

  const [userId,  setUserId]  = useState('');
  const [date,    setDate]    = useState(getTodayISO());
  const [hours,   setHours]   = useState('');
  const [reason,  setReason]  = useState('');
  const [error,   setError]   = useState<string | null>(null);

  const { data: usersData, isLoading: usersLoading } = useQuery({
    queryKey: ['users', 'list-all'],
    queryFn: () => usersService.list({ limit: 100, page: 1, is_active: true }),
  });

  const allUsers = usersData?.data ?? [];

  const mutation = useMutation({
    mutationFn: () =>
      penaltyService.createManual({
        user_id:         userId,
        attendance_date: date,
        penalty_minutes: Math.round(parseFloat(hours) * 60),
        reason:          reason.trim(),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['penalties'] });
      toast.success('Penalty applied successfully');
      onClose();
    },
    onError: (err: unknown) => {
      const msg = err instanceof Error ? err.message : 'Failed to apply penalty';
      setError(msg);
    },
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!userId)                       { setError('Select an employee.'); return; }
    const h = parseFloat(hours);
    if (!hours || isNaN(h) || h <= 0) { setError('Penalty hours must be a positive number.'); return; }
    if (h > 24)                        { setError('Penalty hours cannot exceed 24.'); return; }
    if (!reason.trim())                { setError('Reason is required.'); return; }
    mutation.mutate();
  }

  const submitting = mutation.isPending;
  const selectedUser = allUsers.find((u) => u.id === userId);

  return createPortal(
    // ── Overlay ── true viewport-centered
    <div
      className="fixed inset-0 z-modal flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      onClick={(e) => { if (e.target === e.currentTarget && !submitting) onClose(); }}
    >
      {/* ── Panel ── */}
      <div className="relative w-full max-w-md max-h-[90vh] overflow-y-auto rounded-2xl border border-border bg-popover shadow-2xl">

        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center gap-3 border-b border-border bg-popover px-5 py-4 rounded-t-2xl">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-rose-500/10 border border-rose-500/20">
            <AlertTriangle className="h-4 w-4 text-rose-500 dark:text-rose-400" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-base font-bold text-foreground">Apply Penalty</h2>
            <p className="text-xs text-muted-foreground">Manually deduct hours from employee salary</p>
          </div>
          <button
            onClick={onClose}
            disabled={submitting}
            className="rounded-lg p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors disabled:opacity-40"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-5 py-4 space-y-4">

          {/* Select User */}
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Select User <span className="text-destructive">*</span>
            </label>
            {usersLoading ? (
              <div className="flex h-10 items-center gap-2 rounded-lg border border-input bg-muted/40 px-3 text-sm text-muted-foreground">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading employees…
              </div>
            ) : (
              <Select value={userId} onValueChange={setUserId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a user" />
                </SelectTrigger>
                <SelectContent>
                  {allUsers.map((u) => {
                    const name = [u.first_name, u.last_name].filter(Boolean).join(' ') || u.email;
                    return (
                      <SelectItem key={u.id} value={u.id}>
                        {name}{u.employee_code ? ` (${u.employee_code})` : ''}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            )}
            {selectedUser && (
              <p className="mt-1 text-xs text-muted-foreground">
                {[selectedUser.first_name, selectedUser.last_name].filter(Boolean).join(' ')}
                {selectedUser.employee_code ? ` · ${selectedUser.employee_code}` : ''}
              </p>
            )}
          </div>

          {/* Date + Hours row */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Penalty Date <span className="text-destructive">*</span>
              </label>
              <DatePicker value={date} onChange={setDate} />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Penalty Hours <span className="text-destructive">*</span>
              </label>
              <input
                type="number"
                value={hours}
                onChange={(e) => setHours(e.target.value)}
                min="0.5"
                max="24"
                step="0.5"
                placeholder="e.g., 2.5"
                className={inputCls}
              />
              <p className="mt-1 text-[10px] text-muted-foreground">
                Enter hours to deduct (e.g., 1, 2.5, 4)
              </p>
            </div>
          </div>

          {/* Reason */}
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Reason <span className="text-destructive">*</span>
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Enter the reason for applying penalty..."
              rows={3}
              className={`${inputCls} resize-none`}
            />
          </div>

          {/* Info */}
          <div className="flex items-start gap-2.5 rounded-lg border border-amber-300/50 bg-amber-50 px-3.5 py-2.5 text-xs text-amber-700 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-300">
            <Info className="h-4 w-4 shrink-0 mt-0.5" />
            <span>
              Manual penalties are created as <strong>pending</strong>. An admin must approve before they deduct from salary.
            </span>
          </div>

          {/* Error */}
          {error && (
            <p className="rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-xs font-medium text-destructive">
              {error}
            </p>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end gap-2 border-t border-border pt-3">
            <Button type="button" variant="outline" size="sm" onClick={onClose} disabled={submitting}>
              Cancel
            </Button>
            <Button
              type="submit"
              size="sm"
              disabled={submitting}
              className="bg-rose-500 hover:bg-rose-600 text-white gap-1.5 dark:bg-rose-600 dark:hover:bg-rose-500"
            >
              {submitting && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              Apply Penalty
            </Button>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  );
}
